import { useState, useEffect } from 'react'
import { useNotifications } from '@common/notifications/NotificationContext'
import { apiClient } from '@common/api'

/**
 * Dashboard data hooks — replace AngularJS controllers + factory models.
 *
 * Each hook follows the same pattern as the AngularJS factory:
 * create model → load data → store in state.
 */

// ========== Types ==========

interface BusinessLine {
    name: string
    totalCases: number
    totalTrans: number
    totalAmount: number
}

interface ActiveTransactionData {
    totalActive: number
    totalAmount: number
    businessLines: BusinessLine[]
}

interface AgingReport {
    [key: string]: unknown
}

interface ProvisionalCreditReport {
    [key: string]: unknown
}

interface TeamPerformance {
    [key: string]: unknown
}

// ========== useActiveTransaction ==========

export function useActiveTransaction() {
    const { error: showError } = useNotifications()
    const [data, setData] = useState<ActiveTransactionData | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        apiClient
            .get<ActiveTransactionData>('/activetransaction')
            .then((result) => {
                setData(result)
                setIsLoading(false)
            })
            .catch((err: unknown) => {
                showError(err instanceof Error ? err.message : 'Failed to load active transactions')
                setIsLoading(false)
            })
    }, [showError])

    // Chart data (mirroring the AngularJS controller's chartData)
    const chartData = data
        ? {
            labels: data.businessLines.map((d) => d.name),
            datasets: [
                {
                    data: data.businessLines.map((d) => d.totalTrans),
                    backgroundColor: '#00afd2',
                    borderWidth: 0,
                },
            ],
        }
        : null

    return { data, chartData, isLoading }
}

// ========== useAgingTransaction ==========

export function useAgingTransaction() {
    const { error: showError } = useNotifications()
    const [report, setReport] = useState<AgingReport | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        apiClient
            .get<AgingReport>('/agingtransaction')
            .then((result) => {
                setReport(result)
                setIsLoading(false)
            })
            .catch((err: unknown) => {
                showError(err instanceof Error ? err.message : 'Failed to load aging transactions')
                setIsLoading(false)
            })
    }, [showError])

    return { report, isLoading }
}

// ========== useAgingTransaction120 ==========

export function useAgingTransaction120() {
    const { error: showError } = useNotifications()
    const [report, setReport] = useState<AgingReport | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        apiClient
            .get<AgingReport>('/agingtransaction120')
            .then((result) => {
                setReport(result)
                setIsLoading(false)
            })
            .catch((err: unknown) => {
                showError(err instanceof Error ? err.message : 'Failed to load aging 120 transactions')
                setIsLoading(false)
            })
    }, [showError])

    return { report, isLoading }
}

// ========== useProvisionalCreditRequired ==========

export function useProvisionalCreditRequired() {
    const { error: showError } = useNotifications()
    const [report, setReport] = useState<ProvisionalCreditReport | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        apiClient
            .get<ProvisionalCreditReport>('/provisionalcreditrequired')
            .then((result) => {
                setReport(result)
                setIsLoading(false)
            })
            .catch((err: unknown) => {
                showError(err instanceof Error ? err.message : 'Failed to load provisional credit data')
                setIsLoading(false)
            })
    }, [showError])

    return { report, isLoading }
}

// ========== useDisputeTeamPerformance ==========

export function useDisputeTeamPerformance() {
    const { error: showError } = useNotifications()
    const [teams, setTeams] = useState<TeamPerformance[] | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        apiClient
            .get<TeamPerformance[]>('/disputeteamperformance')
            .then((result) => {
                setTeams(result)
                setIsLoading(false)
            })
            .catch((err: unknown) => {
                showError(err instanceof Error ? err.message : 'Failed to load team performance')
                setIsLoading(false)
            })
    }, [showError])

    return { teams, isLoading }
}
